import { createFileRoute, useParams } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Search, Loader2, Share2, TrendingUp, Users as UsersIcon, DollarSign } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { listSecondTierAffiliates } from "@/lib/contacts.functions";
import { formatCurrency } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/workspaces/$workspaceId/second-tier")({
  component: SecondTierView,
});

function SecondTierView() {
  const { workspaceId } = useParams({ from: "/_authenticated/workspaces/$workspaceId/second-tier" });
  const fetchAffiliates = useServerFn(listSecondTierAffiliates);
  const [search, setSearch] = useState("");

  const { data, isLoading, error } = useQuery({
    queryKey: ["second-tier", workspaceId, search],
    queryFn: () => fetchAffiliates({ data: { workspace_id: workspaceId, search: search || null, limit: 2000 } }),
  });

  const rows = data?.affiliates ?? [];

  const stats = useMemo(() => {
    const total = rows.reduce((s: number, r: any) => s + Number(r.st_total_earned ?? 0), 0);
    const referrals = rows.reduce((s: number, r: any) => s + Number(r.st_referral_count ?? 0), 0);
    const active = rows.filter((r: any) => Number(r.st_referral_count ?? 0) > 0).length;
    return { total, referrals, active, count: rows.length };
  }, [rows]);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Share2 className="h-5 w-5" />
        <h2 className="text-xl font-semibold">2nd Tier Affiliates</h2>
        <span className="text-sm text-muted-foreground">, sub-affiliates whose commissions you earn from</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <StatCard label="Total affiliates" value={String(stats.count)} icon={UsersIcon} />
        <StatCard label="Active (earning)" value={String(stats.active)} icon={TrendingUp} />
        <StatCard label="Total referrals" value={String(stats.referrals)} icon={Share2} />
        <StatCard label="Commissions earned" value={formatCurrency(stats.total)} icon={DollarSign} />
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by name or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-8"
        />
      </div>

      {error ? (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          {(error as Error).message}
        </div>
      ) : isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading affiliates…
        </div>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            No 2nd tier affiliates yet. Upload a second-tier report from Imports to populate this list.
          </CardContent>
        </Card>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead className="text-right">Referrals</TableHead>
                <TableHead className="text-right">Commissions earned</TableHead>
                <TableHead>Last referral</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r: any) => (
                <TableRow key={r.id}>
                  <TableCell className="font-medium">
                    {[r.first_name, r.last_name].filter(Boolean).join(" ") || ","}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{r.email}</TableCell>
                  <TableCell className="text-right">{r.st_referral_count}</TableCell>
                  <TableCell className="text-right font-medium">{formatCurrency(Number(r.st_total_earned ?? 0))}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {r.st_last_referral_at ? new Date(r.st_last_referral_at).toLocaleDateString() : ","}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon }: { label: string; value: string; icon: any }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
            <div className="text-2xl font-semibold mt-1">{value}</div>
          </div>
          <Icon className="h-5 w-5 text-muted-foreground" />
        </div>
      </CardContent>
    </Card>
  );
}
